import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { IntroStatus, ProfileResponse } from '@app/shared';
import { api } from '../lib/api';
import { introOutcome } from '../lib/intro';

const PROFILE_POLL_MS = 2000;

/** The cached profile, re-read every 2 s while the spoken introduction is processed. */
export function useLiveProfile(): ProfileResponse | undefined {
  return useQuery<ProfileResponse>({
    queryKey: ['profile'],
    queryFn: () => api.getProfile(),
    refetchInterval: (q) => (q.state.data?.user.introStatus === 'processing' ? PROFILE_POLL_MS : false),
  }).data;
}

/** Calls `onOutcome` once when a processing introduction ends. */
export function useIntroOutcome(status: IntroStatus | undefined, onOutcome: (outcome: 'done' | 'failed') => void) {
  const prev = useRef(status);
  const callback = useRef(onOutcome);
  callback.current = onOutcome;
  useEffect(() => {
    if (status === undefined) return;
    const outcome = introOutcome(prev.current, status);
    prev.current = status;
    if (outcome) callback.current(outcome);
  }, [status]);
}
